import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav";
import Footer from "../components/Footer";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle, Clock, ArrowRight } from "lucide-react";
import logger from "../lib/logger";

export function ValidatorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [subs, setSubs] = useState([]);
  const [validations, setValidations] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [d, s, v] = await Promise.all([
          API.get("/dashboard"),
          API.get("/submissions"),
          API.get("/validations", { params: { mine: true } }),
        ]);
        setStats(d.data); setSubs(s.data.items || []); setValidations(v.data.items || []);
      } catch (e) { logger.error("Validator dashboard load failed:", e); }
    })();
  }, []);

  const pending = validations.filter((v) => v.status !== "completed");
  const completed = validations.filter((v) => v.status === "completed");

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-10 flex-1 w-full">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest text-muted-h mb-2">Validator</div>
          <h1 className="font-display text-4xl text-ink">Welcome, {user?.first_name}</h1>
          <p className="text-ink-soft mt-1">{user?.profile?.expertise || "Community Validator"}</p>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <Stat label="New Requests" v={stats.new_requests} testid="val-stat-new" accent />
          <Stat label="In Progress" v={stats.in_progress} testid="val-stat-inprog" />
          <Stat label="Completed" v={stats.completed} testid="val-stat-done" />
        </div>

        <section className="mb-8">
          <h2 className="font-display text-2xl text-ink mb-4">Assigned to You</h2>
          {subs.length === 0 ? (
            <div className="card-heritage text-center py-10 text-ink-soft">No validation requests currently assigned.</div>
          ) : (
            <div className="space-y-3">
              {subs.map((s) => (
                <Link key={s.id} to={`/validator/submissions/${s.id}`} className="card-heritage hover:shadow-md flex justify-between items-center transition" data-testid={`val-sub-${s.id}`}>
                  <div>
                    <div className="font-display text-xl text-ink">{s.item?.title || "Untitled"}</div>
                    <div className="text-sm text-muted-h">Contributor: {s.owner_snapshot?.first_name} {s.owner_snapshot?.last_name} · {s.item?.type}</div>
                  </div>
                  <ArrowRight size={18} className="text-terracotta" />
                </Link>
              ))}
            </div>
          )}
        </section>

        {completed.length > 0 && (
          <section>
            <h2 className="font-display text-2xl text-ink mb-4">Recently Completed</h2>
            <div className="space-y-2">
              {completed.slice(0, 8).map((v) => (
                <div key={v.id} className="card-heritage flex justify-between items-center py-3" data-testid={`val-completed-${v.id}`}>
                  <div className="text-ink-soft text-sm flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-700" />
                    Submission {v.submission_id.slice(0, 8)} · Result: <strong>{v.result}</strong>
                  </div>
                  <div className="text-xs text-muted-h">{v.completed_at && new Date(v.completed_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Stat({ label, v, testid, accent }) {
  const val = v ?? 0;
  return (
    <div className={`card-heritage ${accent && val > 0 ? "!border-[var(--heritage-terracotta)]" : ""}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-h">{label}</div>
      <div className={`font-display text-4xl mt-2 ${accent && val > 0 ? "text-terracotta" : "text-ink"}`}>{val}</div>
    </div>
  );
}
